import * as React from 'react'
import { ComponentClass, FunctionComponent, ReactElement, useMemo } from 'react'
import { useFormikContext } from 'formik'
import { Field, makeStyles, mergeClasses, tokens } from '@fluentui/react-components'
import { FieldProps } from '@fluentui/react-field'
import { useTranslation } from 'react-i18next'
import type { Slot } from '@fluentui/react-utilities'
import { Label } from '@fluentui/react-label'

export const useFormikFieldStyles = makeStyles({
    noLabel: {
        gridTemplateColumns: '1fr',
        paddingLeft: '0',
    },
    tinyLabel: {
        gridTemplateColumns: '1fr 28px',
        // The label only takes the first column, so without this the message and the hint land underneath it - in a
        // 50 pixel wide column - instead of underneath the field they belong to.
        '& .fui-Field__validationMessage, & .fui-Field__hint': {
            gridColumnStart: 1,
        },

        '& .fui-Input, .fui-Textarea': {
            gridRowStart: 1,
        },

        '& .fui-InfoLabel': {
            justifySelf: 'center',
            marginTop: tokens.spacingVerticalXS,
        },

        '& .fui-Label__required': {
            display: 'none',
        },
    },
})

export type FormikSubFieldProps<CP, Values> = Omit<FormikFieldProps<CP, Values>, 'component'>

export type FormikFieldProps<CP, Values> = Omit<FieldProps, 'label'> & {
    fieldName: keyof Values
    component: FunctionComponent<CP> | ComponentClass<CP>
    componentProps?: Partial<Omit<CP, 'component' | 'value'>>
    renderLabel?: Slot<typeof Label>
    labelKey?: string
    labelKeyProps?: object
    hintKey?: string
    hintKeyProps?: object
    /**
     * Translation key of the placeholder - the field translates it and adds the required suffix. Give it here rather
     * than as a placeholder in componentProps, so every field handles it the same way.
     */
    placeholderKey?: string
    placeholderKeyProps?: object

    /**
     * Deprecated, but still here so we don't have to change all forms and test them at once.
     * @deprecated
     */
    context?: any
}

export function findValue<Values>(values: Values, fieldName: string): any {
    const fieldPath = fieldName.split('.')
    if (fieldPath.length > 1) {
        return findValue(findPropertyValue(values, fieldPath[0], {}), fieldPath.slice(1).join('.'))
    }
    return findPropertyValue(values, fieldPath[0], '')
}

export function findPropertyValue<Values>(values: Values, fieldName: string, fallbackValue: any): any {
    const fieldPathRegex = /(\w*)(\[(\d+)])/g.exec(fieldName)

    if (fieldPathRegex?.length === 4) {
        const fieldPath = fieldPathRegex[1]
        const fieldPathIndex = fieldPathRegex[3]
        return (values?.[fieldPath] || [])[fieldPathIndex] || fallbackValue
    }
    return values?.[fieldName] || fallbackValue
}

export type FormikFieldPlaceholderProps = {
    required?: boolean
    renderLabel?: Slot<typeof Label>
    labelKey?: string
    placeholderKey?: string
    placeholderKeyProps?: object
    // Forms mark a field as required either on the field or on the control inside it; both count here.
    componentProps?: { required?: boolean; placeholder?: string }
}

/**
 * A field without a label text has nothing that marks it as required - the asterisk sits on the label - so a required
 * field without one says it in its placeholder instead. Only labelKey counts as such a text: renderLabel is used for an
 * InfoLabel that shows nothing but its info icon, so it leaves the field just as unmarked as no label at all.
 */
export function useRequiredPlaceholderSuffix(props: FormikFieldPlaceholderProps): string | undefined {
    const { t: translate } = useTranslation()

    const required = props.required ?? props.componentProps?.required

    return useMemo(() => (required && !props.labelKey ? translate('base.placeholder.required') : undefined), [required, props.labelKey])
}

/**
 * The placeholder of a field, with the required suffix behind it. It comes from placeholderKey; a placeholder that a
 * form still passes through componentProps keeps working and wins, so both spellings end up with the same suffix.
 */
export function useFieldPlaceholder(props: FormikFieldPlaceholderProps, placeholder?: string): string {
    const { t: translate } = useTranslation()
    const suffix = useRequiredPlaceholderSuffix(props)

    return useMemo(() => {
        const value =
            placeholder ??
            props.componentProps?.placeholder ??
            (props.placeholderKey ? translate(props.placeholderKey, { ...props.placeholderKeyProps }) : undefined)
        return value && suffix ? `${value} ${suffix}` : value
    }, [placeholder, props.componentProps?.placeholder, props.placeholderKey, suffix])
}

export default function FormikField<CP, Values>(props: FormikFieldProps<CP, Values>): ReactElement {
    const styles = useFormikFieldStyles()
    const { t: translate } = useTranslation()

    const { values, errors, handleChange, handleBlur } = useFormikContext<Values>()

    const validationMessage = useMemo(() => findValue(errors, props.fieldName as string), [values, errors])
    const validationState = useMemo(() => (findValue(errors, props.fieldName as string) ? 'error' : 'none'), [values, errors])
    const currentValue = useMemo(() => {
        return findValue(values, props.fieldName as string)
    }, [values])

    const noLabel = useMemo(() => !props.renderLabel && !props.labelKey, [props.renderLabel, props.labelKey])
    const placeholderValue = useFieldPlaceholder(props)

    return (
        <Field
            label={props.renderLabel ? props.renderLabel : props.labelKey ? translate(props.labelKey, { ...props.labelKeyProps }) : undefined}
            hint={props.hint ?? (props.hintKey ? translate(props.hintKey, { ...props.hintKeyProps }) : undefined)}
            {...props}
            validationMessage={validationMessage}
            validationState={validationState}
            className={mergeClasses(props.className, noLabel && styles.noLabel)}
        >
            <props.component
                required={props.required}
                onBlur={handleBlur(props.fieldName)}
                onChange={handleChange(props.fieldName)}
                value={currentValue}
                autoComplete="off"
                data-lpignore={'true'}
                {...(props.componentProps as any)}
                {...(placeholderValue ? { placeholder: placeholderValue } : {})}
            />
        </Field>
    )
}
